// src/stores/groupWorkout/index.tsx
import { create } from 'zustand';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import { GroupWorkoutSession, GroupWorkoutHit } from 'interfaces/group-workout';
import { mergeGroupWorkoutHits } from 'lib/group-workout-hits';

export interface GroupWorkoutHitRoundTarget {
  sessionRoundId: string | null;
}

export interface GroupWorkoutSessionStore {
  groupWorkoutSessions: GroupWorkoutSession[];
  currentGroupWorkoutSession: GroupWorkoutSession | null;
  liveGroupWorkoutHits: GroupWorkoutHit[];
  missedGroupWorkoutHitSyncVersion: number;
  loading: boolean;
  error: string | null;
  activeRound: number;
  activeTrack: number;
  actions: {
    setGroupWorkoutSessions: (workoutSessions: GroupWorkoutSession[]) => void;
    setGroupWorkoutSessionById: (workoutSession: GroupWorkoutSession) => void;
    syncGroupWorkoutSessionConnectionState: (workoutSession: GroupWorkoutSession) => void;
    updateGroupWorkoutSession: (workoutSession: GroupWorkoutSession) => void;
    deleteGroupWorkoutSession: (id: string) => void;
    clearCurrentGroupWorkoutSession: () => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    updateActiveRound: (roundNum: number) => void;
    updateActiveTrack: (trackNum: number) => void;
    updateActivePosition: (trackNum: number, roundNum: number) => void;
    addGroupWorkoutHit: (hit: GroupWorkoutHit, target: GroupWorkoutHitRoundTarget) => void;
    clearLiveGroupWorkoutHits: () => void;
    toggleGroupBagEnabled: (groupBagId: string) => void;
  };
}

const MAX_LIVE_GROUP_WORKOUT_HITS = 2000
const compareSortOrder = (a: { sortOrder: number }, b: { sortOrder: number }) => a.sortOrder - b.sortOrder

const normalizeGroupWorkoutSessionOrder = (session: GroupWorkoutSession): GroupWorkoutSession => ({
  ...session,
  groupWorkoutSessionTracks: [...(session.groupWorkoutSessionTracks ?? [])]
    .sort(compareSortOrder)
    .map((track) => ({
      ...track,
      groupWorkoutSessionRounds: [...(track.groupWorkoutSessionRounds ?? [])].sort(compareSortOrder),
    })),
})

const appendLiveGroupWorkoutHit = (
  liveGroupWorkoutHits: GroupWorkoutHit[],
  hit: GroupWorkoutHit,
): GroupWorkoutHit[] => {
  return mergeGroupWorkoutHits(liveGroupWorkoutHits, [hit]).slice(-MAX_LIVE_GROUP_WORKOUT_HITS)
}

const clampIndex = (index: number, length: number) => {
  if (!Number.isFinite(index) || length <= 0) return 0

  return Math.max(0, Math.min(Math.floor(index), length - 1))
}

const getClampedActiveState = (
  currentGroupWorkoutSession: GroupWorkoutSession | null,
  activeTrack: number,
  activeRound: number,
) => {
  const tracks = currentGroupWorkoutSession?.groupWorkoutSessionTracks ?? []
  const nextActiveTrack = clampIndex(activeTrack, tracks.length)
  const rounds = tracks[nextActiveTrack]?.groupWorkoutSessionRounds ?? []
  const nextActiveRound = clampIndex(activeRound, rounds.length)

  return {
    activeTrack: nextActiveTrack,
    activeRound: nextActiveRound,
  }
}

const findSessionRoundLocationById = (
  currentGroupWorkoutSession: GroupWorkoutSession,
  sessionRoundId: string,
) => {
  for (let trackIndex = 0; trackIndex < currentGroupWorkoutSession.groupWorkoutSessionTracks.length; trackIndex += 1) {
    const track = currentGroupWorkoutSession.groupWorkoutSessionTracks[trackIndex]
    const roundIndex = track.groupWorkoutSessionRounds.findIndex((round) => round.id === sessionRoundId)

    if (roundIndex >= 0) {
      return { trackIndex, roundIndex }
    }
  }

  return null
}

const resolveHitRoundLocation = (
  currentGroupWorkoutSession: GroupWorkoutSession,
  target: GroupWorkoutHitRoundTarget,
) => {
  if (target?.sessionRoundId) {
    return findSessionRoundLocationById(currentGroupWorkoutSession, target.sessionRoundId)
  }

  return null
}

const addHitToSessionRound = (
  currentGroupWorkoutSession: GroupWorkoutSession,
  location: { trackIndex: number; roundIndex: number },
  hit: GroupWorkoutHit,
) => {
  const track = currentGroupWorkoutSession.groupWorkoutSessionTracks[location.trackIndex]
  const round = track?.groupWorkoutSessionRounds[location.roundIndex]
  if (!track || !round) return currentGroupWorkoutSession

  const updatedHits = mergeGroupWorkoutHits(round.groupWorkoutHits, [hit])
  if (updatedHits.length === round.groupWorkoutHits.length) return currentGroupWorkoutSession

  const updatedRound = { ...round, groupWorkoutHits: updatedHits }
  const updatedRounds = track.groupWorkoutSessionRounds.map((currentRound, roundIndex) =>
    roundIndex === location.roundIndex ? updatedRound : currentRound
  )
  const updatedTrack = { ...track, groupWorkoutSessionRounds: updatedRounds }
  const updatedTracks = currentGroupWorkoutSession.groupWorkoutSessionTracks.map((currentTrack, trackIndex) =>
    trackIndex === location.trackIndex ? updatedTrack : currentTrack
  )

  return {
    ...currentGroupWorkoutSession,
    groupWorkoutSessionTracks: updatedTracks,
  }
}

const mergeGroupBagConnectionState = (
  currentWorkoutSession: GroupWorkoutSession,
  nextWorkoutSession: GroupWorkoutSession,
): GroupWorkoutSession => {
  const nextGroupBagsById = new Map(nextWorkoutSession.groupBags.map((groupBag) => [groupBag.id, groupBag]))

  const mergedGroupBags = currentWorkoutSession.groupBags.map((currentGroupBag) => {
    const nextGroupBag = nextGroupBagsById.get(currentGroupBag.id)

    if (!nextGroupBag) {
      return currentGroupBag
    }

    return {
      ...currentGroupBag,
      bag: {
        ...currentGroupBag.bag,
        latestWifiStrength: nextGroupBag.bag.latestWifiStrength ?? null,
        averageReceivedPackets: nextGroupBag.bag.averageReceivedPackets ?? null,
        latestRebootCount: nextGroupBag.bag.latestRebootCount ?? null,
      },
    }
  })

  return {
    ...currentWorkoutSession,
    groupBags: mergedGroupBags,
  }
}

const mergeGroupWorkoutSessionSnapshot = (
  currentWorkoutSession: GroupWorkoutSession,
  nextWorkoutSession: GroupWorkoutSession,
  activeTrack: number,
  activeRound: number,
) => {
  let activeRoundAddedHitCount = 0
  const sessionWithConnectionState = mergeGroupBagConnectionState(currentWorkoutSession, nextWorkoutSession)
  const nextTracksById = new Map(nextWorkoutSession.groupWorkoutSessionTracks.map((track) => [track.id, track]))

  const groupWorkoutSessionTracks = sessionWithConnectionState.groupWorkoutSessionTracks.map((currentTrack, trackIndex) => {
    const nextTrack = nextTracksById.get(currentTrack.id) ?? nextWorkoutSession.groupWorkoutSessionTracks[trackIndex]
    if (!nextTrack) return currentTrack

    const nextRoundsById = new Map(nextTrack.groupWorkoutSessionRounds.map((round) => [round.id, round]))
    const groupWorkoutSessionRounds = currentTrack.groupWorkoutSessionRounds.map((currentRound, roundIndex) => {
      const nextRound = nextRoundsById.get(currentRound.id) ?? nextTrack.groupWorkoutSessionRounds[roundIndex]
      if (!nextRound) return currentRound

      const groupWorkoutHits = mergeGroupWorkoutHits(currentRound.groupWorkoutHits, nextRound.groupWorkoutHits)
      const addedHitCount = groupWorkoutHits.length - currentRound.groupWorkoutHits.length
      const updatedRound = {
        ...currentRound,
        roundId: nextRound.roundId ?? currentRound.roundId,
        sortOrder: nextRound.sortOrder ?? currentRound.sortOrder,
        groupWorkoutHits,
      }

      if (trackIndex === activeTrack && roundIndex === activeRound) {
        activeRoundAddedHitCount += addedHitCount
      }

      return updatedRound
    })

    return {
      ...currentTrack,
      trackId: nextTrack.trackId ?? currentTrack.trackId,
      sortOrder: nextTrack.sortOrder ?? currentTrack.sortOrder,
      groupWorkoutSessionRounds,
    }
  })

  return {
    session: {
      ...sessionWithConnectionState,
      groupWorkoutSessionTracks,
    },
    activeRoundAddedHitCount,
  }
}

const useGroupWorkoutSessionStore = create<GroupWorkoutSessionStore>((set, get) => ({
  groupWorkoutSessions: [],
  currentGroupWorkoutSession: null,
  liveGroupWorkoutHits: [],
  missedGroupWorkoutHitSyncVersion: 0,
  loading: false,
  error: null,
  activeRound: 0,
  activeTrack: 0,
  actions: {
    setGroupWorkoutSessions: (workoutSessions: GroupWorkoutSession[]) => set({ groupWorkoutSessions: workoutSessions }),
    setGroupWorkoutSessionById: (workoutSession: GroupWorkoutSession) =>
      set((state) => {
        const normalizedWorkoutSession = normalizeGroupWorkoutSessionOrder(workoutSession)
        const isSameSession = state.currentGroupWorkoutSession?.id === normalizedWorkoutSession.id
        const activeState = isSameSession
          ? getClampedActiveState(normalizedWorkoutSession, state.activeTrack, state.activeRound)
          : { activeTrack: 0, activeRound: 0 }

        return {
          currentGroupWorkoutSession: normalizedWorkoutSession,
          groupWorkoutSessions: [
            ...state.groupWorkoutSessions.filter((groupWorkoutSession) => groupWorkoutSession.id !== normalizedWorkoutSession.id),
            normalizedWorkoutSession,
          ],
          activeTrack: activeState.activeTrack,
          activeRound: activeState.activeRound,
          liveGroupWorkoutHits: isSameSession ? state.liveGroupWorkoutHits : [],
          missedGroupWorkoutHitSyncVersion: isSameSession ? state.missedGroupWorkoutHitSyncVersion : 0,
        }
      }),
    syncGroupWorkoutSessionConnectionState: (workoutSession: GroupWorkoutSession) =>
      set((state) => {
        if (state.currentGroupWorkoutSession?.id !== workoutSession.id) {
          return {
            currentGroupWorkoutSession: state.currentGroupWorkoutSession,
            groupWorkoutSessions: state.groupWorkoutSessions.map((groupWorkoutSession) =>
              groupWorkoutSession.id === workoutSession.id
                ? mergeGroupBagConnectionState(groupWorkoutSession, workoutSession)
                : groupWorkoutSession
            ),
          }
        }

        const activeState = getClampedActiveState(
          state.currentGroupWorkoutSession,
          state.activeTrack,
          state.activeRound,
        )
        const { session, activeRoundAddedHitCount } = mergeGroupWorkoutSessionSnapshot(
          state.currentGroupWorkoutSession,
          workoutSession,
          activeState.activeTrack,
          activeState.activeRound,
        )

        return {
          currentGroupWorkoutSession: session,
          activeTrack: activeState.activeTrack,
          activeRound: activeState.activeRound,
          missedGroupWorkoutHitSyncVersion:
            activeRoundAddedHitCount > 0
              ? state.missedGroupWorkoutHitSyncVersion + 1
              : state.missedGroupWorkoutHitSyncVersion,
          groupWorkoutSessions: state.groupWorkoutSessions.map((groupWorkoutSession) =>
            groupWorkoutSession.id === workoutSession.id
              ? mergeGroupWorkoutSessionSnapshot(
                  groupWorkoutSession,
                  workoutSession,
                  activeState.activeTrack,
                  activeState.activeRound,
                ).session
              : groupWorkoutSession
          ),
        }
      }),
    updateGroupWorkoutSession: (workoutSession: GroupWorkoutSession) =>
      set((state) => {
        const isCurrentSession = state.currentGroupWorkoutSession?.id === workoutSession.id
        const activeState = isCurrentSession
          ? getClampedActiveState(workoutSession, state.activeTrack, state.activeRound)
          : { activeTrack: state.activeTrack, activeRound: state.activeRound }
        const activeScopeChanged =
          activeState.activeTrack !== state.activeTrack || activeState.activeRound !== state.activeRound

        return {
          groupWorkoutSessions: state.groupWorkoutSessions.map((gw) =>
            gw.id === workoutSession.id ? workoutSession : gw
          ),
          currentGroupWorkoutSession: isCurrentSession ? workoutSession : state.currentGroupWorkoutSession,
          activeTrack: activeState.activeTrack,
          activeRound: activeState.activeRound,
          liveGroupWorkoutHits: activeScopeChanged ? [] : state.liveGroupWorkoutHits,
        }
      }),
    deleteGroupWorkoutSession: (id: string) =>
      set((state) => ({
        groupWorkoutSessions: state.groupWorkoutSessions.filter((gw) => gw.id !== id),
        currentGroupWorkoutSession:
          state.currentGroupWorkoutSession?.id === id ? null : state.currentGroupWorkoutSession,
        liveGroupWorkoutHits: state.currentGroupWorkoutSession?.id === id ? [] : state.liveGroupWorkoutHits,
        missedGroupWorkoutHitSyncVersion:
          state.currentGroupWorkoutSession?.id === id ? 0 : state.missedGroupWorkoutHitSyncVersion,
        activeTrack: state.currentGroupWorkoutSession?.id === id ? 0 : state.activeTrack,
        activeRound: state.currentGroupWorkoutSession?.id === id ? 0 : state.activeRound,
      })),
      clearCurrentGroupWorkoutSession: () => set({
        currentGroupWorkoutSession: null,
        liveGroupWorkoutHits: [],
        missedGroupWorkoutHitSyncVersion: 0,
        activeTrack: 0,
        activeRound: 0,
      }),
    setLoading: (loading: boolean) => set({ loading }),
    setError: (error: string | null) => set({ error }),
    updateActiveRound: (roundNum) =>
      set((state) => {
        const activeState = getClampedActiveState(state.currentGroupWorkoutSession, state.activeTrack, roundNum)
        const activeRoundChanged = activeState.activeRound !== state.activeRound

        return {
          activeRound: activeState.activeRound,
          liveGroupWorkoutHits: activeRoundChanged ? [] : state.liveGroupWorkoutHits,
        }
      }),
    updateActiveTrack: (trackNum) =>
      set((state) => {
        const activeState = getClampedActiveState(state.currentGroupWorkoutSession, trackNum, state.activeRound)
        const activeTrackChanged = activeState.activeTrack !== state.activeTrack
        const activeRoundChanged = activeState.activeRound !== state.activeRound

        return {
          activeTrack: activeState.activeTrack,
          activeRound: activeState.activeRound,
          liveGroupWorkoutHits: activeTrackChanged || activeRoundChanged ? [] : state.liveGroupWorkoutHits,
        }
      }),
    updateActivePosition: (trackNum, roundNum) =>
      set((state) => {
        const activeState = getClampedActiveState(state.currentGroupWorkoutSession, trackNum, roundNum)
        const activeTrackChanged = activeState.activeTrack !== state.activeTrack
        const activeRoundChanged = activeState.activeRound !== state.activeRound

        return {
          activeTrack: activeState.activeTrack,
          activeRound: activeState.activeRound,
          liveGroupWorkoutHits: activeTrackChanged || activeRoundChanged ? [] : state.liveGroupWorkoutHits,
        }
      }),
    clearLiveGroupWorkoutHits: () => set({ liveGroupWorkoutHits: [] }),
    addGroupWorkoutHit: (hit, target) =>
      set((state) => {
        const { currentGroupWorkoutSession } = state;
        const liveGroupWorkoutHits = appendLiveGroupWorkoutHit(state.liveGroupWorkoutHits, hit)

        if (!currentGroupWorkoutSession) {
          console.log("No current workout session");
          return { liveGroupWorkoutHits };
        }

        const hitRoundLocation = resolveHitRoundLocation(currentGroupWorkoutSession, target)
        if (!hitRoundLocation) return { liveGroupWorkoutHits };

        const updatedSession = addHitToSessionRound(currentGroupWorkoutSession, hitRoundLocation, hit)
        if (updatedSession === currentGroupWorkoutSession) return { liveGroupWorkoutHits };

        return {
          currentGroupWorkoutSession: updatedSession,
          groupWorkoutSessions: state.groupWorkoutSessions.map((groupWorkoutSession) =>
            groupWorkoutSession.id === updatedSession.id ? updatedSession : groupWorkoutSession
          ),
          liveGroupWorkoutHits,
        };
      }),
    toggleGroupBagEnabled: (groupBagId: string) =>
      set((state) => {
        const { currentGroupWorkoutSession } = state;
        if (!currentGroupWorkoutSession?.groupBags) return {};
  
        // create updated groupBags
        const updatedBags = currentGroupWorkoutSession.groupBags.map((groupBag) =>
          groupBag.id === groupBagId ? { ...groupBag, isEnabled: !groupBag.isEnabled } : groupBag
        );
  
        const updatedSession = {
          ...currentGroupWorkoutSession,
          groupBags: updatedBags,
        };
        return {
          currentGroupWorkoutSession: updatedSession,
          // also update any matching session in groupWorkoutSessions
          groupWorkoutSessions: state.groupWorkoutSessions.map((gw) =>
            gw.id === updatedSession.id ? updatedSession : gw
          ),
        };
      }),
  },
  
}));

// Enable Zustand devtools in development
if (process.env.NODE_ENV === 'development') {
  mountStoreDevtool('GroupWorkoutSessionStore', useGroupWorkoutSessionStore);
}

export default useGroupWorkoutSessionStore;
export type { GroupWorkoutSession };
